/**
 * ExploreCategoryBigPreview - Big preview with image, title, and vertical entry list
 * Replicates the native app's ExploreCategoryBigPreview category type
 * Created by neo on 02.04.26
 */
import * as React from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { useLocalized } from '../../Hooks/useLocalized';
import { useLocalizedImage } from '../../Hooks/useLocalizedMedia';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { MarkdownRenderer } from '../Markdown/MarkdownRenderer';
import { useCategoryEntriesSuspense } from '../../Hooks/useCategoryEntriesSuspense';
import { CinematicCard } from './CinematicCard';
import { getMediaLargeOrMediumOrSmallest } from '../../Model/Media/Media';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.div<{ isLast?: boolean }>`
  padding: 48px 0;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #f0f0f0')};
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroImage = styled.div<{ src?: string }>`
  width: 100%;
  height: 280px;
  border-radius: 16px;
  background: ${({ src }) => (src ? `url(${src})` : 'linear-gradient(135deg, #00dda2 0%, #007763 100%)')};
  background-size: cover;
  background-position: center;
  margin-bottom: 24px;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.01);
  }

  @media (max-width: 768px) {
    height: 200px;
    border-radius: 12px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0;
  flex: 1;
  line-height: 1.2;
`;

const ShowAllButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${Theme.Colors.lighterGrey};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  color: ${Theme.Colors.textColor};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.primary};
    color: white;
  }
`;

const Description = styled.div`
  font-size: 1rem;
  color: #666;
  margin-bottom: 24px;
  max-width: 600px;
  line-height: 1.6;
`;

const EntriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CardWrapper = styled.div<{ delay: number }>`
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ delay }) => delay}ms;
  opacity: 0;
`;

const SkeletonCard = styled.div`
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

type BigPreviewContentProps = {
  category: ExploreCategory;
  isLast?: boolean;
  linkPrefix: string;
};

const BigPreviewContent: React.FC<BigPreviewContentProps> = observer(
  ({ category, isLast, linkPrefix }) => {
    const { t } = useTrans();
    const navigate = useNavigate();
    const title = useLocalized(category.name);
    const shortDescription = useLocalized(category.shortDescription);
    const longDescription = useLocalized(category.description);
    const description = shortDescription || longDescription;
    const image = useLocalizedImage(category.images);
    const imageUrl = image ? getMediaLargeOrMediumOrSmallest(image) : undefined;

    const { entries } = useCategoryEntriesSuspense(category.id, category.identifier);

    const handleShowAll = React.useCallback(() => {
      navigate(`${linkPrefix}${category.identifier}`);
    }, [navigate, linkPrefix, category.identifier]);

    if (entries.length === 0) {
      return null;
    }

    return (
      <Section isLast={isLast}>
        <Container>
          {imageUrl && <HeroImage src={imageUrl} onClick={handleShowAll} />}
          <HeaderRow>
            <Title>{title}</Title>
            <ShowAllButton onClick={handleShowAll} title={t('globals.showAll', { defaultValue: 'Show All' })}>
              →
            </ShowAllButton>
          </HeaderRow>
          {description && (
            <Description>
              <MarkdownRenderer text={description} />
            </Description>
          )}
          <EntriesGrid>
            {entries.slice(0, 4).map((entry, index) => (
              <CardWrapper key={entry.id} delay={index * 100}>
                <CinematicCard entry={entry} linkPrefix={linkPrefix} />
              </CardWrapper>
            ))}
          </EntriesGrid>
        </Container>
      </Section>
    );
  },
);

export type ExploreCategoryBigPreviewProps = {
  category: ExploreCategory;
  isLast?: boolean;
  linkPrefix?: string;
};

export const ExploreCategoryBigPreview: React.FC<ExploreCategoryBigPreviewProps> = observer(
  ({ category, isLast, linkPrefix = '/' }) => {
    return (
      <Suspense
        fallback={
          <Section>
            <Container>
              <EntriesGrid>
                {Array.from({ length: 2 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </EntriesGrid>
            </Container>
          </Section>
        }
      >
        <BigPreviewContent category={category} isLast={isLast} linkPrefix={linkPrefix} />
      </Suspense>
    );
  },
);
